import { createContext, useState, useEffect, ReactNode, useContext, useRef, useCallback } from 'react';
import axios from '../axios-config';

export interface StoreType {
    storeTypeId: number;
    storeTypeName: string;
}

interface StoreTypesContextValue {
    storeTypes: StoreType[];
    fetchStoreTypes: () => Promise<void>;
}

const StoreTypesContext = createContext<StoreTypesContextValue>({ storeTypes: [], fetchStoreTypes: async () => {} });

export const StoreTypesProvider = ({ children }: { children: ReactNode }) => {
    const [storeTypes, setStoreTypes] = useState<StoreType[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const isInitializedRef = useRef(false);

    const fetchStoreTypes = useCallback(async () => {
        if (isLoading || isInitializedRef.current) {
            console.log("Skipping fetchStoreTypes: Already loading or initialized");
            return;
        }
        console.log("Fetching store types...");
        setIsLoading(true);
        try {
            const { data } = await axios.get('/api/StoreTypes');
            console.log("Store types fetched:", data);
            setStoreTypes(data);
            isInitializedRef.current = true;
        } catch (err: any) {
            console.error('Error fetching store types:', err.message);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading]);

    useEffect(() => {
        fetchStoreTypes();
    }, [fetchStoreTypes]); // Run only once on mount

    return (
        <StoreTypesContext.Provider value={{ storeTypes, fetchStoreTypes }}>
            {children}
        </StoreTypesContext.Provider>
    );
};

export const useStoreTypes = () => useContext(StoreTypesContext);
